import { createSelector } from "@reduxjs/toolkit";
import { activeMembersAdapter } from "@store/active-members/active-members-slice";
import { getActiveMemberKeys } from "@utils/xg-seat-utils";

/**
 * Returns all the stored active members.
 */
export const activeMembersSelector = createSelector(state => state.activeMembers, activeMembers => {
  return activeMembersAdapter.getSelectors().selectAll(activeMembers);
});

/**
 * Returns the fetching properties of the active members slice.
 */
export const fetchingActiveMembersFlagsSelector = createSelector(state => state.activeMembers, activeMembers => {
  return activeMembers.fetching;
});

/** Whether the stored active members may be outdated and should be fetched again while shown */
export function areActiveMembersOutdatedSelector(state) {
  return state.activeMembers.isOutdated;
}

/** Whether the stored active members are being fetched again in the background */
export function isRefreshingActiveMembersSelector(state) {
  return state.activeMembers.refreshStatus === "pending";
}

/**
 * Whether the stored active members tell who is an active user now: they were fetched, and nothing
 * changed since that their refresh has not brought yet. While they are outdated, being refreshed or
 * could not be refreshed, they may still list a member who is no longer an active user, or miss one.
 */
export function areActiveMembersCurrentSelector(state) {
  const {
    fetching,
    isOutdated,
    refreshStatus
  } = state.activeMembers;
  return fetching.status === "succeeded" && !isOutdated && refreshStatus !== "pending" && refreshStatus !== "rejected";
}

/**
 * Returns whether the active members are not available yet, either because they have not been
 * requested yet or because the request is still running.
 *
 * Everything that shows active members, e.g. their table and the export of that table, has to
 * treat both cases as loading. Otherwise an action would already be offered on the empty state
 * that is held until the members arrive.
 */
export const areActiveMembersLoadingSelector = createSelector(fetchingActiveMembersFlagsSelector, ({
  status
}) => {
  return status === "pending" || status === "uninitialized";
});

/**
 * Returns the keys to look members up in the active members, see getActiveMemberKeys.
 */
export const activeMemberKeysSelector = createSelector(activeMembersSelector, activeMembers => {
  return getActiveMemberKeys(activeMembers);
});